import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getLocalizedValue } from "../config/languageConfig";
import { useCategories } from "../hooks/useCategories";
import useTranslation from "../hooks/useTranslation";
import { Category } from "../types/category";
import EmptyState from "./EmptyState";
import ErrorState from "./ErrorState";
import ImageWithFallback from "./ImageWithFallback";
import LoadingState from "./LoadingState";

// SOON 카테고리 타입 정의
interface SoonCategory {
  id: string;
  isSoon: true;
}

// 통합 카테고리 타입
type DisplayCategory = Category | SoonCategory;

const CategorySection: React.FC = () => {
  const { categories, loading, error, refetch } = useCategories();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 전체 카테고리 배열 생성 (실제 카테고리 + SOON 카테고리)
  const displayCategories = useMemo(() => {
    if (!categories || categories.length === 0) {
      return [];
    }

    const totalSlots = 10; // 2행 × 5열
    const actualCategories: DisplayCategory[] = [...categories];

    // 부족한 슬롯만큼 SOON 카테고리 추가
    const soonCount = Math.max(0, totalSlots - categories.length);
    for (let i = 0; i < soonCount; i++) {
      actualCategories.push({
        id: `soon-${i}`,
        isSoon: true,
      });
    }

    return actualCategories;
  }, [categories]);

  // Handle loading state
  if (loading) {
    return <LoadingState />;
  }

  // Handle error state
  if (error) {
    return <ErrorState message={t("categories.error")} onRetry={refetch} />;
  }

  // Handle empty state
  if (!categories || categories.length === 0) {
    return <EmptyState message={t("categories.empty")} />;
  }

  // 카테고리 클릭 핸들러
  const handleCategoryClick = (category: DisplayCategory) => {
    // SOON 카테고리는 클릭 불가
    if ("isSoon" in category) {
      return;
    }

    if (category.routeType === "direct-shop" && category.directShopId) {
      // giftshop 카테고리: 가게 상세 페이지로 직접 이동
      navigate(`/restaurant/${category.directShopId}`);
    } else {
      // 일반 카테고리: 가게 리스트 페이지로 이동
      navigate(`/categories/${category.id}`);
    }
  };

  // 카테고리 이름 다국어 처리
  const getLocalizedCategoryName = (category: Category) => {
    // API에서 반환되는 각 언어별 이름 필드를 사용
    const translations = {
      en: category.nameEn,
      ko: category.nameKo,
      ja: category.nameJa,
      "zh-CN": category.nameZhCn,
      "zh-TW": category.nameZhTw,
    };

    // 현재 언어에 맞는 이름 반환 (없으면 기본 이름 사용)
    return getLocalizedValue(category.name, translations);
  };

  return (
    <div className="md-spacing-2">
      <div className="grid grid-cols-5 gap-2">
        {displayCategories.map((category) => {
          // SOON 카테고리 렌더링
          if ("isSoon" in category) {
            return (
              <div
                key={category.id}
                className="flex flex-col items-center py-1 px-1 opacity-60 min-h-[88px] justify-start"
              >
                {/* SOON 이미지 플레이스홀더 */}
                <div className="w-[60px] h-[60px] mb-2 rounded-lg overflow-hidden  shadow-sm  border-gray-300">
                  <ImageWithFallback
                    src="/home/images/categories/coming.png"
                    alt="Coming Soon"
                    className="w-full h-full object-cover"
                    fallback="/images/categories/coming.png"
                  />
                </div>

                {/* SOON 텍스트 */}
                <h3 className="text-[10px] text-center text-gray-500 leading-tight">
                  {t("categories.comingSoon")}
                </h3>
              </div>
            );
          }

          // 일반 카테고리 렌더링
          return (
            <div
              key={category.id}
              className="flex flex-col items-center cursor-pointer py-1 px-1 hover:opacity-80 transition-opacity min-h-[88px] justify-start"
              onClick={() => handleCategoryClick(category)}
              role="button"
              tabIndex={0}
              aria-label={`${getLocalizedCategoryName(category)} 카테고리 선택`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCategoryClick(category);
                }
              }}
            >
              {/* Category Image */}
              <div className="w-[60px] h-[60px] mb-2 rounded-lg overflow-hidden shadow-sm">
                <ImageWithFallback
                  src={category.imageUrl}
                  alt={getLocalizedCategoryName(category)}
                  className="w-full h-full object-cover"
                  fallback={[
                    "/home/images/categories/coming.png",
                    "/images/categories/coming.png"
                  ]}
                />
              </div>

              {/* Category Name */}
              <h3 className="text-[10px] text-center text-gray-800 leading-tight max-w-full">
                <span
                  className="block overflow-hidden text-ellipsis"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {getLocalizedCategoryName(category)}
                </span>
              </h3>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategorySection;
